import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  BookOpen, 
  Play, 
  FileText, 
  Users, 
  Clock, 
  Star,
  Search,
  Filter,
  Eye,
  Download,
  Shield,
  Lock,
  AlertTriangle,
  Wifi,
  Mail,
  Key,
  Database,
  Smartphone,
  Globe,
  CheckCircle,
  ArrowRight,
  PlayCircle,
  BookmarkPlus,
  Share2,
  Upload,
  Plus,
  Edit3,
  Save,
  X,
  Video,
  Image,
  HelpCircle,
  Award,
  Target,
  Trash2,
  Edit,
  TrendingUp,
  Brain,
  BarChart3
} from 'lucide-react'
import { videoUploadService, formatFileSize, formatDuration, getVideoQuality } from '../services/videoUploadService'
import { courseService, formatCategoryName, getDifficultyColor } from '../services/courseService'
import { quizService, getQuestionTypeLabel, getScoreColor } from '../services/quizService'
import { articleService } from '../services/articleService'
import { infographicsService } from '../services/infographicsService'
import { useAuth } from '../context/AuthContext'
import { useUser } from '../context/UserContext'
import Articles from '../components/Articles'
import Infographics from '../components/Infographics'

const LearningCenter = () => {
  const { user, profile } = useAuth()
  const { canCreate, canEdit, canDelete, canUpdate } = useUser()
  
  // Map user_type to role for component compatibility
  const getUserRole = () => {
    if (!profile?.user_type) return 'user'
    
    const roleMapping = {
      'it_administrator': 'admin',
      'security_officer': 'admin', 
      'department_head': 'editor',
      'compliance_officer': 'editor',
      'government_employee': 'user'
    }
    
    return roleMapping[profile.user_type] || 'user'
  }
  
  const userRole = getUserRole()
  
  // Debug logging to help troubleshoot role mapping
  useEffect(() => {
    console.log('LearningCenter Debug:', {
      user: user?.id,
      profile: profile,
      userType: profile?.user_type,
      mappedRole: userRole
    })
  }, [user, profile, userRole])
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedDifficulty, setSelectedDifficulty] = useState('all')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showQuizModal, setShowQuizModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedContent, setSelectedContent] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [quizAnswers, setQuizAnswers] = useState({})
  const [quizScore, setQuizScore] = useState(null)
  
  // New state for real data
  const [courses, setCourses] = useState([])
  const [videos, setVideos] = useState([]) // All learning videos for viewing
  const [userVideos, setUserVideos] = useState([]) // User's uploaded videos for management
  const [quizzes, setQuizzes] = useState([])
  const [articles, setArticles] = useState([])
  const [infographics, setInfographics] = useState([])
  const [userCourses, setUserCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadMetadata, setUploadMetadata] = useState({
    title: '',
    description: '',
    category: 'general',
    difficulty: 'beginner',
    courseId: null
  })
  const [currentQuizAttempt, setCurrentQuizAttempt] = useState(null)
  const [quizTimeRemaining, setQuizTimeRemaining] = useState(null)
  const [showQuizResults, setShowQuizResults] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [currentVideo, setCurrentVideo] = useState(null)
  const fileInputRef = useRef(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(9) // 3x3 grid
  const [totalPages, setTotalPages] = useState(1)
  
  // Load data on component mount
  useEffect(() => {
    loadData()
  }, [])

  // Reload user data when authentication state changes
  useEffect(() => {
    if (user) {
      // Load user-specific data when user signs in
      const loadUserData = async () => {
        try {
          const userCoursesData = await courseService.getUserEnrolledCourses()
          setUserCourses(userCoursesData)
        } catch (userErr) {
          console.warn('Error loading user courses:', userErr)
          setUserCourses([])
        }
      }
      loadUserData()
    } else {
      // Clear user data when user signs out
      setUserCourses([])
    }
  }, [user])

  // Load courses, videos, and quizzes
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load public data that doesn't require authentication
      const [coursesData, quizzesData, articlesData, infographicsData] = await Promise.all([
        courseService.getCourses({ isPublished: true }),
        quizService.getQuizzes({ isPublished: true }),
        articleService.getAllArticles(),
        infographicsService.getAllInfographics()
      ])
      
      setCourses(coursesData)
      setQuizzes(quizzesData)
      setArticles(articlesData || [])
      setInfographics(infographicsData || [])

      // Only load user-specific data if authenticated
      if (user) {
        try {
          const [userCoursesData, allVideosData, userVideosData] = await Promise.all([
            courseService.getUserEnrolledCourses(),
            videoUploadService.getAllLearningVideos(), // Get all videos for viewing
            videoUploadService.getUserVideos() // Get user's videos for management
          ])

          setUserCourses(userCoursesData)
          setVideos(allVideosData || [])
          setUserVideos(userVideosData || [])
          console.log(`Loaded ${allVideosData?.length || 0} learning videos and ${userVideosData?.length || 0} user videos`)
        } catch (userErr) {
          console.error('Error loading user data:', userErr)
          // Don't fail the entire load for user-specific data
          setUserCourses([])
          setVideos([])
          setUserVideos([])
        }
      } else {
        setUserCourses([])
        setVideos([])
        setUserVideos([])
      }
    } catch (err) {
      console.error('Error loading data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }
  
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    type: 'video',
    category: 'phishing',
    difficulty: 'beginner',
    content: '',
    file: null
  })

  const tabs = [
    { id: 'all', label: `All Content (${videos.length + articles.length + infographics.length + quizzes.length})`, icon: BookOpen },
    { id: 'videos', label: `Videos (${videos.length})`, icon: Play },
    { id: 'articles', label: `Articles (${articles.length})`, icon: FileText },
    { id: 'infographics', label: `Infographics (${infographics.length})`, icon: Eye },
    { id: 'quizzes', label: `Quizzes (${quizzes.length})`, icon: CheckCircle }
  ]

  const categories = [
    { id: 'all', label: 'All Topics', icon: BookOpen },
    { id: 'phishing', label: 'Phishing & Email Security', icon: Mail },
    { id: 'passwords', label: 'Password Security', icon: Key },
    { id: 'data-protection', label: 'Data Protection', icon: Database },
    { id: 'mobile-security', label: 'Mobile Security', icon: Smartphone },
    { id: 'network-security', label: 'Network Security', icon: Wifi },
    { id: 'social-engineering', label: 'Social Engineering', icon: Users },
    { id: 'incident-response', label: 'Incident Response', icon: AlertTriangle },
    { id: 'compliance', label: 'Compliance & Governance', icon: Shield }
  ]

  const difficulties = [
    { id: 'all', label: 'All Levels' },
    { id: 'beginner', label: 'Beginner' },
    { id: 'intermediate', label: 'Intermediate' },
    { id: 'advanced', label: 'Advanced' }
  ]

  // Sample quiz data
  const sampleQuiz = {
    id: 4,
    title: 'Mobile Device Security Quiz',
    questions: [
      {
        id: 1,
        question: 'What is the most important security feature to enable on your mobile device?',
        options: [
          'Screen lock with PIN/password',
          'Bluetooth connectivity',
          'Location services',
          'Auto-brightness'
        ],
        correct: 0
      },
      {
        id: 2,
        question: 'Which of the following is a sign of a malicious app?',
        options: [
          'Requests excessive permissions',
          'Has many positive reviews',
          'Is available on official app stores',
          'Has a professional interface'
        ],
        correct: 0
      },
      {
        id: 3,
        question: 'What should you do before connecting to public Wi-Fi?',
        options: [
          'Connect immediately for convenience',
          'Verify the network name with staff',
          'Share the password with others',
          'Disable all security features'
        ],
        correct: 1
      }
    ]
  }

  // Handler functions
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    setUploadFiles(files)
    setUploadProgress(0)
    setError(null)
  }

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) return

    try {
      setUploadProgress(0)
      setError(null)

      if (uploadFiles.length === 1) {
        // Single file upload
        const file = uploadFiles[0]
        const result = await videoUploadService.uploadVideo(file, uploadMetadata, (progress) => {
          setUploadProgress(progress.percentage)
        })

        if (result.success) {
          await loadData() // Refresh data
          setShowUploadModal(false)
          setUploadFiles([])
          setUploadMetadata({
            title: '',
            description: '',
            category: 'general',
            difficulty: 'beginner',
            courseId: null
          })
          setUploadProgress(0)
        } else {
          setError(result.error || 'Upload failed')
        }
      } else {
        // Multiple file upload
        const results = await videoUploadService.uploadMultipleVideos(uploadFiles, uploadMetadata, (progress) => {
          setUploadProgress(progress.overallProgress)
        })

        const successCount = results.filter(r => r.success).length
        const failedCount = results.length - successCount
        
        if (successCount > 0) {
          await loadData() // Refresh data
          
          if (failedCount === 0) {
            // All uploads successful
            setShowUploadModal(false)
            setUploadFiles([])
            setUploadMetadata({
              title: '',
              description: '',
              category: 'general',
              difficulty: 'beginner',
              courseId: null
            })
            setUploadProgress(0)
          } else {
            // Some uploads failed
            setError(`${successCount} file(s) uploaded successfully, ${failedCount} failed.`)
            setUploadProgress(0)
          }
        } else {
          // All uploads failed
          setError('All uploads failed. Please try again.')
          setUploadProgress(0)
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
      setError(error.message)
      setUploadProgress(0)
    }
  }

  const handleQuizAnswer = (questionId, answerIndex) => {
    setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex })
  }

  const submitQuiz = async () => {
    try {
      if (!currentQuizAttempt) {
        console.error('No quiz attempt found')
        return
      }

      const submissionData = {
        quizId: currentQuizAttempt.quizId,
        attemptId: currentQuizAttempt.id,
        answers: quizAnswers,
        timeSpent: currentQuizAttempt.timeLimit * 60 - (quizTimeRemaining || 0)
      }

      const result = await quizService.submitQuizAttempt(submissionData)
      
      if (result.success) {
        setQuizScore(result.score)
        setShowQuizResults(true)
        setCurrentQuizAttempt(null)
        setQuizTimeRemaining(null)
      }
    } catch (error) {
      console.error('Error submitting quiz:', error)
      setError(error.message)
    }
  }

  // Quiz timer effect
  useEffect(() => {
    let timer
    if (quizTimeRemaining > 0 && showQuizModal && !showQuizResults) {
      timer = setInterval(() => {
        setQuizTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - auto submit quiz
            submitQuiz()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [quizTimeRemaining, showQuizModal, showQuizResults])

  const createContent = async () => {
    try {
      setLoading(true)
      
      // Check authentication before creating content
      if (!user) {
        setError('You must be signed in to create content. Please sign in and try again.')
        return
      }
      
      let result = null
      
      if (newContent.type === 'course') {
        const courseData = {
          title: newContent.title,
          description: newContent.description,
          category: newContent.category,
          difficulty: newContent.difficulty,
          isPublished: true
        }
        
        result = await courseService.createCourse(courseData)
      } else if (newContent.type === 'quiz') {
        const quizData = {
          title: newContent.title,
          description: newContent.description,
          category: newContent.category,
          difficulty: newContent.difficulty,
          timeLimit: 30, // 30 minutes default
          isPublished: true
        }
        
        result = await quizService.createQuiz(quizData)
      }
      
      // Refresh data to show the newly created content
      await loadData()
      
      // Close modal and reset form
      setShowCreateModal(false)
      setNewContent({
        title: '',
        description: '',
        type: 'course',
        category: 'phishing',
        difficulty: 'beginner',
        content: '',
        file: null
      })
      
      // Show success message
      if (result) {
        setError(null)
        // You could add a success toast notification here
      }
    } catch (error) {
      console.error('Error creating content:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const startContent = async (content) => {
    try {
      setSelectedContent(content)
      
      if (content.type === 'quiz') {
        // Check authentication for quiz attempts
        if (!user) {
          setError('You must be signed in to take quizzes. Please sign in and try again.')
          return
        }
        
        // Start a new quiz attempt
        const attempt = await quizService.startQuizAttempt(content.id)
        
        if (attempt.success) {
          setCurrentQuizAttempt(attempt.data)
          setQuizTimeRemaining(attempt.data.timeLimit * 60) // Convert to seconds
          setShowQuizModal(true)
          setQuizAnswers({})
          setQuizScore(null)
          setShowQuizResults(false)
          
          // Start timer
          const timer = setInterval(() => {
            setQuizTimeRemaining(prev => {
              if (prev <= 1) {
                clearInterval(timer)
                submitQuiz() // Auto-submit when time runs out
                return 0
              }
              return prev - 1
            })
          }, 1000)
        }
      } else if (content.type === 'course') {
        // Check authentication for course enrollment
        if (!user) {
          setError('You must be signed in to enroll in courses. Please sign in and try again.')
          return
        }
        
        // Enroll in course if not already enrolled
        if (!userCourses.find(uc => uc.courseId === content.id)) {
          await courseService.enrollInCourse(content.id)
          await loadData() // Refresh data
        }
        
        // Navigate to course content or show course details
        alert(`Starting course: ${content.title}`)
      } else if (content.type === 'video') {
        // For videos from database, show in modal player
        if (content.video_url) {
          try {
            setError(null) // Clear any previous errors
            
            // Check if user is authenticated
            if (!user) {
              setError('You must be signed in to watch videos. Please sign in and try again.')
              return
            }
            
            // Generate signed URL for private video content
            const signedUrl = await videoUploadService.getVideoSignedUrl(content.video_url)
            
            setCurrentVideo({
              ...content,
              video_url: signedUrl
            })
            setShowVideoModal(true)
          } catch (error) {
            console.error('Error generating video URL:', error)
            
            // Provide specific error messages based on the error type
            if (error.message.includes('Authentication required')) {
              setError('Authentication required. Please sign in to watch videos.')
            } else if (error.message.includes('Invalid video URL')) {
              setError(`Video file not found: ${content.title}. The video may have been moved or deleted.`)
            } else if (error.message.includes('Failed to generate video access URL')) {
              setError(`Access denied: You don't have permission to view this video. Please contact your administrator.`)
            } else {
              setError(`Unable to load video: ${content.title}. Please try again later or contact support.`)
            }
          }
        } else {
          setError(`Video not available: ${content.title}. No video file found.`)
        }
      } else {
        // For articles, infographics, etc. (no authentication required for viewing)
        alert(`Starting ${content.type}: ${content.title}`)
      }
    } catch (error) {
      console.error('Error starting content:', error)
      setError(error.message)
    }
  }

  const learningContent = [
    {
      id: 1,
      title: 'Recognizing Phishing Emails: A Complete Guide',
      description: 'Learn how to identify and avoid phishing attacks that target government employees.',
      type: 'video',
      category: 'phishing',
      difficulty: 'beginner',
      duration: '12 minutes',
      views: 15420,
      rating: 4.8,
      thumbnail: '/api/placeholder/400/225',
      author: 'DICT Cybersecurity Team',
      publishDate: '2024-01-20',
      tags: ['Phishing', 'Email Security', 'Awareness'],
      featured: true,
      completed: false,
      preview: 'This comprehensive video guide covers the most common phishing techniques and provides practical tips for identifying suspicious emails...'
    },
    {
      id: 2,
      title: 'Creating Strong Passwords: Best Practices',
      description: 'Essential guidelines for creating and managing secure passwords in government systems.',
      type: 'article',
      category: 'passwords',
      difficulty: 'beginner',
      duration: '8 minutes',
      views: 12350,
      rating: 4.7,
      thumbnail: '/api/placeholder/400/225',
      author: 'IT Security Division',
      publishDate: '2024-01-18',
      tags: ['Passwords', 'Authentication', 'Security'],
      featured: true,
      completed: true,
      preview: 'Learn the fundamentals of password security, including length requirements, complexity, and the use of password managers...'
    },
    {
      id: 3,
      title: 'Data Privacy Laws in the Philippines',
      description: 'Understanding RA 10173 and its implications for government data handling.',
      type: 'infographic',
      category: 'data-protection',
      difficulty: 'intermediate',
      duration: '5 minutes',
      views: 8920,
      rating: 4.9,
      thumbnail: '/api/placeholder/400/225',
      author: 'National Privacy Commission',
      publishDate: '2024-01-15',
      tags: ['Data Privacy', 'RA 10173', 'Compliance'],
      featured: false,
      completed: false,
      preview: 'Visual guide to understanding the key provisions of the Data Privacy Act and how they apply to government operations...'
    },
    {
      id: 4,
      title: 'Mobile Device Security Quiz',
      description: 'Test your knowledge of mobile security best practices for government devices.',
      type: 'quiz',
      category: 'mobile-security',
      difficulty: 'intermediate',
      duration: '10 minutes',
      views: 6780,
      rating: 4.6,
      thumbnail: '/api/placeholder/400/225',
      author: 'DICT Training Team',
      publishDate: '2024-01-12',
      tags: ['Mobile Security', 'BYOD', 'Assessment'],
      featured: false,
      completed: false,
      preview: 'Interactive quiz covering mobile device management, app security, and safe usage practices for government employees...'
    },
    {
      id: 5,
      title: 'Incident Response: First Steps',
      description: 'What to do when you suspect a cybersecurity incident in your office.',
      type: 'video',
      category: 'incident-response',
      difficulty: 'beginner',
      duration: '15 minutes',
      views: 9450,
      rating: 4.8,
      thumbnail: '/api/placeholder/400/225',
      author: 'Emergency Response Team',
      publishDate: '2024-01-10',
      tags: ['Incident Response', 'Emergency', 'Protocol'],
      featured: true,
      completed: false,
      preview: 'Step-by-step guide on immediate actions to take when a cybersecurity incident is detected...'
    },
    {
      id: 6,
      title: 'Social Engineering Tactics and Defense',
      description: 'Understanding manipulation techniques used by cybercriminals.',
      type: 'article',
      category: 'social-engineering',
      difficulty: 'intermediate',
      duration: '12 minutes',
      views: 7230,
      rating: 4.7,
      thumbnail: '/api/placeholder/400/225',
      author: 'Behavioral Security Unit',
      publishDate: '2024-01-08',
      tags: ['Social Engineering', 'Psychology', 'Defense'],
      featured: false,
      completed: false,
      preview: 'Explore common social engineering techniques and learn how to recognize and defend against them...'
    },
    {
      id: 7,
      title: 'Network Security Fundamentals',
      description: 'Basic concepts of network security for government IT administrators.',
      type: 'video',
      category: 'network-security',
      difficulty: 'advanced',
      duration: '25 minutes',
      views: 5670,
      rating: 4.9,
      thumbnail: '/api/placeholder/400/225',
      author: 'Network Security Team',
      publishDate: '2024-01-05',
      tags: ['Network Security', 'Firewalls', 'VPN'],
      featured: false,
      completed: false,
      preview: 'Comprehensive overview of network security principles, including firewalls, intrusion detection, and secure communications...'
    },
    {
      id: 8,
      title: 'Compliance Checklist Infographic',
      description: 'Visual checklist for maintaining cybersecurity compliance in government offices.',
      type: 'infographic',
      category: 'compliance',
      difficulty: 'beginner',
      duration: '3 minutes',
      views: 11200,
      rating: 4.5,
      thumbnail: '/api/placeholder/400/225',
      author: 'Compliance Division',
      publishDate: '2024-01-03',
      tags: ['Compliance', 'Checklist', 'Standards'],
      featured: false,
      completed: true,
      preview: 'Easy-to-follow visual checklist covering essential compliance requirements for government cybersecurity...'
    }
  ]

  // Combine all content types for filtering - use only real database data
  const allContent = [
    ...courses.map(course => ({ ...course, type: 'course' })),
    ...videos.map(video => ({ 
      ...video, 
      type: 'video',
      category: video.courses?.category || 'general',
      difficulty: video.courses?.difficulty || 'beginner',
      author: 'User Upload',
      publishDate: video.created_at ? new Date(video.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      tags: video.courses?.tags || ['Video'],
      featured: false,
      completed: false,
      thumbnail: video.thumbnail_url || '/api/placeholder/400/225',
      duration: video.duration ? `${Math.floor(video.duration / 60)} minutes` : 'Unknown',
      views: 0, // Real view count would come from analytics
      rating: 0, // Real rating would come from user feedback
      preview: video.description || 'User uploaded video content'
    })),
    ...articles.map(article => ({
      ...article,
      type: 'article',
      publishDate: article.created_at ? new Date(article.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      thumbnail: article.featured_image || '/api/placeholder/400/225',
      preview: article.content ? article.content.substring(0, 150) + '...' : article.description || 'Article content'
    })),
    ...infographics.map(infographic => ({
      ...infographic,
      type: 'infographic',
      publishDate: infographic.created_at ? new Date(infographic.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      thumbnail: infographic.featured_image || '/api/placeholder/400/225',
      preview: infographic.description || 'Infographic content'
    })),
    ...quizzes.map(quiz => ({ ...quiz, type: 'quiz' }))
  ]

  const filteredContent = allContent.filter(content => {
    const matchesTab = activeTab === 'all' || content.type === activeTab || 
                      (activeTab === 'videos' && content.type === 'video') ||
                      (activeTab === 'articles' && content.type === 'article') ||
                      (activeTab === 'infographics' && content.type === 'infographic') ||
                      (activeTab === 'quizzes' && content.type === 'quiz')
    const matchesSearch = content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (content.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (content.tags && content.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    const matchesCategory = selectedCategory === 'all' || content.category === selectedCategory
    const matchesDifficulty = selectedDifficulty === 'all' || content.difficulty === selectedDifficulty
    
    return matchesTab && matchesSearch && matchesCategory && matchesDifficulty
  })

  // Pagination logic
  const totalItems = filteredContent.length
  const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedContent = filteredContent.slice(startIndex, endIndex)

  // Update total pages when filtered content changes
  useEffect(() => {
    setTotalPages(calculatedTotalPages)
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredContent.length, itemsPerPage, calculatedTotalPages, currentPage])

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, searchTerm, selectedCategory, selectedDifficulty])



  const featuredContent = allContent.filter(content => content.featured)

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return Play
      case 'article': return FileText
      case 'infographic': return Eye
      case 'quiz': return CheckCircle
      default: return BookOpen
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'bg-red-100 text-red-700'
      case 'article': return 'bg-blue-100 text-blue-700'
      case 'infographic': return 'bg-green-100 text-green-700'
      case 'quiz': return 'bg-purple-100 text-purple-700'
      default: return 'bg-gov-100 text-gov-700'
    }
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-success-100 text-success-700'
      case 'intermediate': return 'bg-warning-100 text-warning-700'
      case 'advanced': return 'bg-red-100 text-red-700'
      default: return 'bg-gov-100 text-gov-700'
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-cyber-600 rounded-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-gov-800 mb-4">
            Cyber Tips & Learning Corner
          </h1>
          <p className="text-xl text-gov-600 max-w-3xl mx-auto mb-8">
            Enhance your cybersecurity knowledge with our comprehensive collection of videos, articles, 
            infographics, and interactive content designed for government professionals.
          </p>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {canCreate() && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-secondary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Create Content</span>
              </button>
            )}
            {/* Removed Take Quiz button per user request */}
          </div>
        </motion.div>

        {/* Featured Content removed per user request */}

        {/* Tabs - restored up to Infographics (excluding Quizzes) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.filter(tab => tab.id !== 'quizzes').map((tab) => {
              const TabIcon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-cyber-600 text-white'
                      : 'bg-white text-gov-600 hover:bg-cyber-50'
                  }`}
                >
                  <TabIcon size={16} />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </motion.div>

        {/* Upload Video Button - Only visible in Videos tab and for users with create permissions */}
        {activeTab === 'videos' && canCreate() && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-end mb-4"
          >
            <button
              onClick={() => {
                if (!user) {
                  setError('You must be signed in to upload videos. Please sign in and try again.')
                  return
                }
                setShowUploadModal(true)
              }}
              className="btn-primary flex items-center space-x-2 text-sm px-3 py-2"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Video</span>
            </button>
          </motion.div>
        )}

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gov-800">Search & Filter Content</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gov-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search learning content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-field"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Difficulty Filter */}
            <div>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="input-field"
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty.id} value={difficulty.id}>
                    {difficulty.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {activeTab !== 'articles' && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gov-200">
              <span className="text-sm text-gov-600">
                {activeTab === 'infographics' ? 
                  `${infographics.length} items found • Showing 1-${Math.min(infographics.length, itemsPerPage)} of ${infographics.length}` :
                  `${filteredContent.length} items found • Showing ${startIndex + 1}-${Math.min(endIndex, totalItems)} of {totalItems}`
                }
              </span>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gov-600">Items per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="px-2 py-1 border border-gov-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyber-500"
                >
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                  <option value={12}>12</option>
                  <option value={18}>18</option>
                </select>
              </div>
              
              {/* Top Pagination Controls */}
              {filteredContent.length > 0 && totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gov-300 rounded text-sm font-medium text-gov-700 bg-white hover:bg-gov-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else {
                        if (currentPage <= 3) {
                          page = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          page = totalPages - 4 + i;
                        } else {
                          page = currentPage - 2 + i;
                        }
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            currentPage === page
                              ? 'bg-cyber-600 text-white'
                              : 'text-gov-700 bg-white border border-gov-300 hover:bg-gov-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gov-300 rounded text-sm font-medium text-gov-700 bg-white hover:bg-gov-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </motion.div>

        {/* Content Grid - Conditional Rendering Based on Active Tab */}
        {activeTab === 'articles' ? (
          <Articles 
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            selectedDifficulty={selectedDifficulty}
            userRole={userRole}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage)
              setCurrentPage(1)
            }}
          />
        ) : activeTab === 'infographics' ? (
          <Infographics 
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            selectedDifficulty={selectedDifficulty}
            userRole={userRole}
          />
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence>
              {paginatedContent.map((content) => {
                const TypeIcon = getTypeIcon(content.type)
                return (
                  <motion.div
                    key={content.id}
                    variants={itemVariants}
                    layout
                    className="card group hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                  >
                    <div className="relative mb-4">
                      <div className="w-full h-48 bg-gradient-to-br from-cyber-100 to-gov-100 rounded-lg flex items-center justify-center">
                        <TypeIcon className="w-16 h-16 text-cyber-600" />
                      </div>
                      <div className="absolute top-3 left-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(content.type)}`}>
                          {content.type.toUpperCase()}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(content.difficulty)}`}>
                          {content.difficulty}
                        </span>
                      </div>
                      {content.completed && (
                        <div className="absolute bottom-3 right-3">
                          <div className="w-8 h-8 bg-success-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                        <PlayCircle className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-gov-800 mb-2 group-hover:text-cyber-600 transition-colors duration-200">
                      {content.title}
                    </h3>
                    
                    <p className="text-sm text-gov-600 mb-3 line-clamp-2">
                      {content.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {content.tags.slice(0, 2).map((tag, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-gov-100 text-gov-700 rounded">
                          {tag}
                        </span>
                      ))}
                      {content.tags.length > 2 && (
                        <span className="px-2 py-1 text-xs bg-gov-100 text-gov-700 rounded">
                          +{content.tags.length - 2}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gov-500 mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>{content.duration}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Eye className="w-3 h-3" />
                          <span>{content.views.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-warning-500 fill-current" />
                        <span>{content.rating}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gov-500 mb-4">
                      <span>{content.author}</span>
                      <span>{new Date(content.publishDate).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => startContent(content)}
                        className="btn-primary flex-1 flex items-center justify-center space-x-2 text-sm py-2"
                      >
                        <PlayCircle size={14} />
                        <span>{content.completed ? 'Review' : 'Start'}</span>
                      </button>
                      <button className="btn-secondary flex items-center justify-center space-x-2 text-sm py-2 px-3">
                        <BookmarkPlus size={14} />
                      </button>
                      <button className="btn-secondary flex items-center justify-center space-x-2 text-sm py-2 px-3">
                        <Share2 size={14} />
                      </button>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Bottom Pagination Controls - Hidden as requested by user */}
        {/* User requested to use top pagination instead of bottom pagination */}

        {/* No Results */}
        {filteredContent.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <BookOpen className="w-16 h-16 text-gov-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gov-600 mb-2">No content found</h3>
            <p className="text-gov-500 mb-4">Try adjusting your search terms or filters.</p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('all')
                setSelectedDifficulty('all')
                setActiveTab('all')
              }}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          </motion.div>
        )}

      {/* Upload Video Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gov-800">Upload Video Content</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gov-400 hover:text-gov-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Authentication Status Check */}
                {!user && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">Authentication Required</span>
                    </div>
                    <p className="text-sm text-red-600 mt-2">
                      You must be signed in to upload videos. Please sign in to your account and try again.
                    </p>
                  </div>
                )}
                
                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    user 
                      ? 'border-gov-300 hover:border-cyber-400 cursor-pointer' 
                      : 'border-gray-200 cursor-not-allowed opacity-50'
                  }`}
                  onClick={() => user && fileInputRef.current?.click()}
                >
                  <Upload className="w-12 h-12 text-gov-400 mx-auto mb-4" />
                  <p className="text-gov-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-sm text-gov-500">MP4, WebM, AVI, MOV up to 500MB</p>
                  {uploadFiles.length > 0 && (
                    <div className="mt-4 text-sm text-cyber-600">
                      {uploadFiles.length} file(s) selected
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />

                {/* Metadata Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gov-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={uploadMetadata.title}
                      onChange={(e) => setUploadMetadata(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                      placeholder="Enter video title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gov-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={uploadMetadata.description}
                      onChange={(e) => setUploadMetadata(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                      rows={3}
                      placeholder="Enter video description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-1">
                        Category
                      </label>
                      <select
                        value={uploadMetadata.category}
                        onChange={(e) => setUploadMetadata(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                      >
                        {categories.filter(cat => cat.id !== 'all').map(category => (
                          <option key={category.id} value={category.id}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-1">
                        Difficulty
                      </label>
                      <select
                        value={uploadMetadata.difficulty}
                        onChange={(e) => setUploadMetadata(prev => ({ ...prev, difficulty: e.target.value }))}
                        className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                      >
                        {difficulties.filter(diff => diff.id !== 'all').map(level => (
                          <option key={level.id} value={level.id}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Course Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gov-700 mb-1">
                      Add to Course (Optional)
                    </label>
                    <select
                      value={uploadMetadata.courseId || ''}
                      onChange={(e) => setUploadMetadata(prev => ({ ...prev, courseId: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                    >
                      <option value="">Select a course (optional)</option>
                      {courses.filter(course => course.created_by === user?.id).map(course => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gov-200 rounded-full h-2">
                      <div
                        className="bg-cyber-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Uploaded Videos Table */}
                {user && videos.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-gov-800 mb-4 flex items-center space-x-2">
                      <Video className="w-5 h-5" />
                      <span>Your Uploaded Videos ({videos.length})</span>
                    </h4>
                    <div className="max-h-64 overflow-y-auto border border-gov-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gov-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-gov-700 font-medium">Title</th>
                            <th className="px-3 py-2 text-left text-gov-700 font-medium">Duration</th>
                            <th className="px-3 py-2 text-left text-gov-700 font-medium">Size</th>
                            <th className="px-3 py-2 text-left text-gov-700 font-medium">Quality</th>
                            <th className="px-3 py-2 text-left text-gov-700 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userVideos.map((video, index) => (
                            <tr key={video.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gov-25'}>
                              <td className="px-3 py-2">
                                <div className="flex items-center space-x-2">
                                  <Play className="w-4 h-4 text-cyber-600" />
                                  <div>
                                    <div className="font-medium text-gov-800 truncate max-w-32" title={video.title}>
                                      {video.title}
                                    </div>
                                    {video.description && (
                                      <div className="text-xs text-gov-500 truncate max-w-32" title={video.description}>
                                        {video.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-gov-600">
                                {formatDuration(video.duration || 0)}
                              </td>
                              <td className="px-3 py-2 text-gov-600">
                                {formatFileSize(video.file_size || 0)}
                              </td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyber-100 text-cyber-800">
                                  {video.resolution ? getVideoQuality(video.resolution) : 'Unknown'}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => window.open(video.video_url, '_blank')}
                                    className="p-1 text-cyber-600 hover:text-cyber-800 hover:bg-cyber-50 rounded"
                                    title="View Video"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {canDelete() && (
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Are you sure you want to delete this video?')) {
                                          try {
                                            await videoUploadService.deleteVideo(video.id)
                                            await loadData() // Refresh the video list
                                          } catch (error) {
                                            console.error('Error deleting video:', error)
                                            setError('Failed to delete video: ' + error.message)
                                          }
                                        }
                                      }}
                                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                      title="Delete Video"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* No Videos Message */}
                {user && userVideos.length === 0 && (
                  <div className="mt-6 p-4 bg-gov-50 border border-gov-200 rounded-lg text-center">
                    <Video className="w-8 h-8 text-gov-400 mx-auto mb-2" />
                    <p className="text-gov-600 font-medium">No videos uploaded yet</p>
                    <p className="text-sm text-gov-500">Upload your first video to get started</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowUploadModal(false)
                      setUploadFiles([])
                      setUploadProgress(0)
                      setError(null)
                    }}
                    className="flex-1 px-4 py-2 border border-gov-300 text-gov-700 rounded-lg hover:bg-gov-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!user) {
                        setError('You must be signed in to upload videos. Please sign in and try again.')
                        return
                      }
                      if (uploadFiles.length > 0) {
                        handleUploadSubmit()
                      }
                    }}
                    disabled={!user || uploadFiles.length === 0 || uploadProgress > 0}
                    className="flex-1 px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!user ? 'Sign in required to upload videos' : ''}
                  >
                    {!user ? 'Sign In Required' : uploadProgress > 0 ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Content Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gov-800">Create New Content</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gov-400 hover:text-gov-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Content Type
                  </label>
                  <select
                    value={newContent.type}
                    onChange={(e) => setNewContent({ ...newContent, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  >
                    <option value="course">Course</option>
                    <option value="quiz">Quiz</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Category
                  </label>
                  <select
                    value={newContent.category}
                    onChange={(e) => setNewContent({ ...newContent, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  >
                    {categories.filter(cat => cat.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={newContent.title}
                  onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  placeholder="Enter content title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newContent.description}
                  onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  rows="3"
                  placeholder="Enter content description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gov-700 mb-2">
                  Difficulty Level
                </label>
                <select
                  value={newContent.difficulty}
                  onChange={(e) => setNewContent({ ...newContent, difficulty: e.target.value })}
                  className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                >
                  {difficulties.filter(diff => diff.id !== 'all').map(level => (
                    <option key={level.id} value={level.id}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quiz-specific fields */}
              {newContent.type === 'quiz' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Time Limit (minutes)
                      </label>
                      <input
                        type="number"
                        value={newContent.timeLimit || ''}
                        onChange={(e) => setNewContent({ ...newContent, timeLimit: parseInt(e.target.value) || null })}
                        className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                        placeholder="Optional"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Passing Score (%)
                      </label>
                      <input
                        type="number"
                        value={newContent.passingScore || 70}
                        onChange={(e) => setNewContent({ ...newContent, passingScore: parseInt(e.target.value) || 70 })}
                        className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowRetakes"
                      checked={newContent.allowRetakes || false}
                      onChange={(e) => setNewContent({ ...newContent, allowRetakes: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="allowRetakes" className="text-sm text-gov-700">
                      Allow multiple attempts
                    </label>
                  </div>
                </>
              )}

              {/* Course-specific fields */}
              {newContent.type === 'course' && (
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-2">
                    Estimated Duration (hours)
                  </label>
                  <input
                    type="number"
                    value={newContent.estimatedDuration || ''}
                    onChange={(e) => setNewContent({ ...newContent, estimatedDuration: parseFloat(e.target.value) || null })}
                    className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                    placeholder="e.g., 2.5"
                    min="0.1"
                    step="0.1"
                  />
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setError(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gov-300 text-gov-700 rounded-md hover:bg-gov-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createContent}
                  disabled={!newContent.title || !newContent.description || loading}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {showQuizModal && currentQuizAttempt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gov-800">{currentQuizAttempt.quiz?.title}</h3>
              <div className="flex items-center space-x-4">
                {quizTimeRemaining > 0 && (
                  <div className="flex items-center space-x-2 text-gov-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-mono">
                      {Math.floor(quizTimeRemaining / 60)}:{(quizTimeRemaining % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowQuizModal(false)
                    setCurrentQuizAttempt(null)
                    setQuizTimeRemaining(0)
                  }}
                  className="text-gov-400 hover:text-gov-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {!showQuizResults ? (
              <div className="space-y-6">
                {currentQuizAttempt.quiz?.questions?.map((question, index) => (
                  <div key={question.id} className="border border-gov-200 rounded-lg p-4">
                    <h4 className="font-medium text-gov-800 mb-3">
                      {index + 1}. {question.question_text}
                    </h4>
                    
                    {question.question_type === 'multiple_choice' && (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => (
                          <label key={optionIndex} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={optionIndex}
                              checked={quizAnswers[question.id] === optionIndex}
                              onChange={() => handleQuizAnswer(question.id, optionIndex)}
                              className="text-cyber-600 focus:ring-cyber-500"
                            />
                            <span className="text-gov-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    {question.question_type === 'true_false' && (
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            value="true"
                            checked={quizAnswers[question.id] === 'true'}
                            onChange={() => handleQuizAnswer(question.id, 'true')}
                            className="text-cyber-600 focus:ring-cyber-500"
                          />
                          <span className="text-gov-700">True</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            value="false"
                            checked={quizAnswers[question.id] === 'false'}
                            onChange={() => handleQuizAnswer(question.id, 'false')}
                            className="text-cyber-600 focus:ring-cyber-500"
                          />
                          <span className="text-gov-700">False</span>
                        </label>
                      </div>
                    )}
                    
                    {question.question_type === 'short_answer' && (
                      <textarea
                        value={quizAnswers[question.id] || ''}
                        onChange={(e) => handleQuizAnswer(question.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gov-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-500"
                        rows={3}
                        placeholder="Enter your answer..."
                      />
                    )}
                  </div>
                ))}
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowQuizModal(false)
                      setCurrentQuizAttempt(null)
                      setQuizTimeRemaining(0)
                    }}
                    className="flex-1 px-4 py-2 border border-gov-300 text-gov-700 rounded-md hover:bg-gov-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitQuiz}
                    disabled={Object.keys(quizAnswers).length !== currentQuizAttempt.quiz?.questions?.length}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit Quiz
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gov-100 rounded-full">
                  <Award className={`w-10 h-10 ${quizScore >= (currentQuizAttempt.quiz?.passing_score || 70) ? 'text-green-600' : 'text-yellow-600'}`} />
                </div>
                
                <div>
                  <h4 className="text-2xl font-bold text-gov-800 mb-2">Quiz Complete!</h4>
                  <p className="text-lg text-gov-600">
                    Your Score: <span className={`font-bold ${quizScore >= (currentQuizAttempt.quiz?.passing_score || 70) ? 'text-green-600' : 'text-yellow-600'}`}>
                      {quizScore}%
                    </span>
                  </p>
                  <p className="text-sm text-gov-500 mt-2">
                    {quizScore >= (currentQuizAttempt.quiz?.passing_score || 70) ? 'Excellent work! You passed!' : 'Good effort! Consider reviewing the material and trying again.'}
                  </p>
                  {currentQuizAttempt.quiz?.allow_retakes && quizScore < (currentQuizAttempt.quiz?.passing_score || 70) && (
                    <p className="text-sm text-cyber-600 mt-1">
                      You can retake this quiz to improve your score.
                    </p>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  {currentQuizAttempt.quiz?.allow_retakes && (
                    <button
                      onClick={() => {
                        setShowQuizResults(false)
                        setQuizAnswers({})
                        setQuizScore(null)
                        startContent(currentQuizAttempt.quiz)
                      }}
                      className="flex-1 px-4 py-2 border border-gov-300 text-gov-700 rounded-md hover:bg-gov-50"
                    >
                      Retake Quiz
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowQuizModal(false)
                      setShowQuizResults(false)
                      setCurrentQuizAttempt(null)
                      setQuizAnswers({})
                      setQuizScore(null)
                      setQuizTimeRemaining(0)
                      loadData() // Refresh data to show updated progress
                    }}
                    className="flex-1 btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && currentVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{currentVideo.title}</h3>
              <button
                onClick={() => {
                  setShowVideoModal(false)
                  setCurrentVideo(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                <video
                  controls
                  autoPlay
                  className="w-full h-full"
                  src={currentVideo.video_url}
                  onError={(e) => {
                    console.error('Video loading error:', e)
                    console.error('Video URL:', currentVideo.video_url)
                    console.error('User info:', { userId: user?.id, userType: profile?.user_type })
                    
                    // Show error message in video container
                    const videoContainer = e.target.parentElement
                    videoContainer.innerHTML = `
                      <div class="flex items-center justify-center h-full text-white bg-gray-900">
                        <div class="text-center p-8">
                          <div class="mb-4">
                            <svg class="w-16 h-16 mx-auto text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                          <h3 class="text-lg font-semibold mb-2 text-red-400">Video Not Available</h3>
                          <p class="text-gray-300 text-sm mb-4">The video file could not be loaded. This may be due to:</p>
                          <ul class="text-gray-400 text-xs text-left space-y-1 mb-4">
                            <li>• Network connectivity issues</li>
                            <li>• Video file permissions</li>
                            <li>• File has been moved or deleted</li>
                            <li>• Storage service temporarily unavailable</li>
                          </ul>
                          <p class="text-gray-400 text-xs">Please try refreshing the page or contact your administrator if the problem persists.</p>
                        </div>
                      </div>
                    `
                  }}
                  onLoadStart={() => {
                    console.log('Video loading started for:', currentVideo.title)
                  }}
                  onCanPlay={() => {
                    console.log('Video can play:', currentVideo.title)
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              {currentVideo.description && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-600">{currentVideo.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default LearningCenter
